// the 'any' in the normalizer is because the data is coming from the API
// brb using zod to give type to api data

import { PostModel } from '@dialogueconsulting/sked-preview'
import { format } from 'date-fns'
import orderBy from 'lodash.orderby'

import groupPostsByDate from 'utils/groupPostsByDate'

export const normalizeSetting = (setting: any) => {
  return {
    ...setting,
    companyName: setting.company,
    isConversationsEnabled: setting.isConversationsEnabled,
    isReadOnly: setting.isReadOnly,
  }
}

export const mapPlatform = (platform: string) => {
  switch (platform) {
    case 'FB':
      return 'facebook'
    case 'GMB':
      return 'googleBusiness'
    case 'IG':
      return 'instagram'
    case 'LI':
      return 'linkedin'
    case 'PI':
      return 'pinterest'
    case 'SC':
      return 'snapchat'
    case 'TW':
      return 'twitter'
    case 'TT':
      return 'tiktok'
    case 'YT':
      return 'youtube'
    case 'OT':
      return 'multiplePlatforms'
    default:
      return 'multiplePlatforms'
  }
}

export const deMapPlatform = (platform: string) => {
  switch (platform) {
    case 'facebook':
      return 'FB'
    case 'googleBusiness':
      return 'GMB'
    case 'instagram':
      return 'IG'
    case 'linkedin':
      return 'LI'
    case 'pinterest':
      return 'PI'
    case 'snapchat':
      return 'SC'
    case 'twitter':
      return 'TW'
    case 'tiktok':
      return 'TT'
    case 'youtube':
      return 'YT'
    case 'multiplePlatforms':
      return 'OT'
    default:
      return 'OT'
  }
}

export const normalizeDateToRowId = (date: string) =>
  format(new Date(date), 'dd-MM-yyyy')

export const datePlaceholderForDraftPost = '00-00-0000'

export const normalizePosts = (raw: any, isReadOnly: boolean) => {
  const data = groupPostsByDate(raw)

  let postIndex = 1

  let posts = data.map((item: any) => ({
    id:
      item.date === 'drafts'
        ? datePlaceholderForDraftPost
        : normalizeDateToRowId(item.date),
    date:
      item.date === 'drafts'
        ? datePlaceholderForDraftPost
        : format(new Date(item.date), 'EEEE, do MMMM'),
    isVisible: true,
    posts: item.posts.map((post: any) => {
      return {
        postIndex: postIndex++,
        id: post.id,
        status: post.status.type,
        time:
          item.date === 'drafts'
            ? datePlaceholderForDraftPost
            : format(new Date(post.when), 'h:mm a'),
        accounts: post.accounts.map((account: any) => ({
          avatar: account.avatar,
          name: account.name,
          avatarBorderColor: account?.color || '#000000',
        })),
        caption: post.caption,
        platform: (() => {
          let shortFormPlatform = ''
          const isSinglePlatform = post.accounts.every(
            (val: any, _: number, arr: any) =>
              val.platform == arr[arr.length - 1].platform
          )
          shortFormPlatform = isSinglePlatform
            ? post.accounts[0].platform
            : 'multiplePlatforms'
          return mapPlatform(shortFormPlatform)
        })(),
        media: post.media,
        isUndo: post.status.type !== 'pending',
        isSelected: false,
        isVisible: true,
        when: post.when,
      }
    }),
  }))

  if (isReadOnly) {
    posts = posts.filter((p) => p.date !== '00-00-0000')
  }

  return posts
}

export const normalizeAccounts = (settings: any) => {
  const result: any = []
  settings.accounts.forEach((account: any) => {
    const obj = {
      id: account.id, // use account.name for <AccountSelector />,
      name: account.name,
      avatar: account.avatar,
      platform: mapPlatform(account.platform),
      avatarBorderColor: account.color,
      checked: true,
    }

    if (!account.group) {
      result.push(obj)
    } else {
      const index = result.findIndex((o: any) => o.name === account.group)
      if (index === -1) {
        result.push({
          id: account.group,
          name: account.name,
          checked: true,
          group_options: [obj],
        })
      } else {
        if (!result[index].group_options) {
          result[index].group_options = []
        }
        result[index].group_options.push(obj)
      }
    }
  })

  return result
}

export interface StatusKey {
  pending: string
  approved: string
  requestChange: string
}

export const normalizeStatusKey = (settings: any): StatusKey => {
  return {
    pending: settings.statuses.pending?.key,
    approved: settings.statuses.approved?.key,
    requestChange: settings.statuses.requestChange?.key,
  }
}

export const normalizeSkedPreviewPost = (post: any, accounts: any) => {
  // return multiple sked preview as array because a draft post can have multiple platforms
  if (Array.isArray(post)) {
    return post
      .filter((item) => item)
      .map((p) => {
        const account = accounts.find(
          (account: any) => account.id === p?.account_id
        )

        const currentUser = {
          username: account.name,
          profile_picture_url: account.avatar,
        }

        return {
          ...new PostModel(
            p,
            {
              id: account.id,
              profile_picture_url: account.avatar,
              username: account.name,
            },
            undefined,
            undefined
          ),
          accountId: account.id,
          collaboratingIgUsers: getCollaboratingIgUsers(post, currentUser),
        }
      })
  } else {
    const account = accounts.find(
      (account: any) => account.id === post?.account_id
    )

    if (!account) {
      throw new Error('Account not found')
    }
    const currentUser = {
      username: account.name,
      profile_picture_url: account.avatar,
    }

    return {
      ...new PostModel(
        post,
        {
          id: account.id,
          profile_picture_url: account.avatar,
          username: account.name,
        },
        undefined,
        undefined
      ),
      collaboratingIgUsers: getCollaboratingIgUsers(post, currentUser),
    }
  }
}
function getCollaboratingIgUsers(
  p: any,
  currentUser: {
    username: string
    profile_picture_url: string
  }
) {
  return p.collaboratingIgUsers && Array.isArray(p.collaboratingIgUsers)
    ? [
        {
          username: currentUser.username,
          profilePictureUrl: currentUser.profile_picture_url,
        },
        ...p.collaboratingIgUsers,
      ]
    : [
        {
          username: currentUser.username,
          profilePictureUrl: currentUser.profile_picture_url,
        },
      ]
}
export const normalizeMessages = (comments: any) => {
  if (!comments) {
    return []
  }

  const result = comments.map((comment: any) => {
    return {
      id: comment.id,
      text:
        comment.type === 'StatusChanged'
          ? `${comment.createdBy.name} changed status from <strong>${comment.oldStatus.label}</strong> to <strong>${comment.newStatus.label}</strong>`
          : comment.text,
      indicatorColor: comment?.newStatus?.color || 'transparent',
      user: {
        avatar: '', // todo make this as initial using comment.createdBy.name
        name: comment.createdBy.name,
      },
      datetime: comment.createdAt,
      isMessageOwner: comment.isMessageOwner,
      isRejected: comment.type !== 'UserWritten',
      isRejectedMessage: comment?.newStatus?.type === 'requestChange',
      isRejectedComment: comment.type === 'StatusChanged' ? comment.text : '',
    }
  })

  return result
}

export const normalizeRepliedMessage = (data: any) => {
  return {
    id: data.id,
    text: `${data.createdBy.name} changed status from <strong>${data.oldStatus.label}</strong> to <strong>${data.newStatus.label}</strong>`,
    user: {
      avatar: '', // todo make this as initial using comment.createdBy.name
      name: data.createdBy.name,
    },
    datetime: data.createdAt,
    isMessageOwner: true,
    isRejected: true,
    isRejectedMessage: data?.newStatus?.type === 'requestChange',
    isRejectedComment: data.text,
    indicatorColor: data.newStatus.color,
  }
}

export const isPostIsStory = (post: any) =>
  ((post.isStory || post.type === 'story') && !post.storyType) ||
  ((post.isStory || post.type === 'story') &&
    post.storyType &&
    post.storyType === 'story')

export const normalizeGridPreview = (data: any, selectedPostId: string) => {
  const { name, avatar } = data[0].accounts[0]
  const result = data
    .map((post: any) => {
      return {
        id: post.id,
        status: post.isPosted ? 'posted' : post.status.type,
        media: post.media,
        isSelected: post.id === selectedPostId,
      }
    })
    .filter((post: any) => !isPostIsStory(post))

  return {
    account: {
      name,
      handler_name: '',
      avatar,
    },
    posts: orderBy(result, 'isSelected', 'desc'),
  }
}

export const normalizeGridPreviewAllPosts = (data: any) => {
  return data.map((post: any) => ({
    accounts: post.accounts.map((account: any) => account.id),
    accountInfo: {
      avatar: post.accounts.find((_: any, index: any) => index === 0).avatar,
      name: post.accounts.find((_: any, index: any) => index === 0).name,
      handler_name: '',
    },
    id: post.id,
    status: post.isPosted ? 'posted' : post.status.type,
    media: post.media,
  }))
}

export const normalizeCalendarEvents = (data: any) => {
  return data.map((post: any) => ({
    id: post.id,
    start: new Date(post.when),
    end: new Date(post.when),
    tileData: {
      platform: mapPlatform(post.accounts[0]?.platform),
      status: post.status.type,
      mediaSrc: post.media.src,
      time: format(new Date(post.when), 'h:mm a'),
      type: post.media.type,
      caption: post.caption,
      hasNotification: post.hasNotification,
    },
  }))
}
